import { parseTRPCError } from '@/lib/utils/trpc-error-utils';

export type BulkSyncStoreRef = {
  id: string;
  name: string;
  provider: ProductSyncProvider;
};

export type ProductSyncProvider = 'shopify' | 'woocommerce';

export type IntegrationRow = {
  provider: string;
  enabled: boolean;
};

export type BulkSyncIntegrationsResult = {
  started: number;
  failed: Array<{ storeId: string; storeName: string; reason: string }>;
};

export type SyncEligibility = {
  eligible: BulkSyncStoreRef[];
  ignored: Array<{ id: string; name: string }>;
};

export const resolveSyncProviderFromIntegrations = (
  integrations: IntegrationRow[] | undefined,
): ProductSyncProvider | null => {
  const enabled = integrations?.filter((integration) => integration.enabled) ?? [];
  if (enabled.some((integration) => integration.provider === 'woocommerce')) return 'woocommerce';
  if (enabled.some((integration) => integration.provider === 'shopify')) return 'shopify';
  return null;
};

export const buildSyncEligibility = (
  stores: Array<{ id: string; name: string }>,
  integrationsByStoreId: Record<string, IntegrationRow[] | undefined>,
): SyncEligibility => {
  const eligible: BulkSyncStoreRef[] = [];
  const ignored: Array<{ id: string; name: string }> = [];
  stores.forEach((store) => {
    const provider = resolveSyncProviderFromIntegrations(integrationsByStoreId[store.id]);
    if (provider) {
      eligible.push({ id: store.id, name: store.name, provider });
      return;
    }
    ignored.push({ id: store.id, name: store.name });
  });
  return { eligible, ignored };
};

const isRateLimitError = (message: string) =>
  message.includes('Daily sync limit') || message.includes('Please wait');

const isInngestError = (message: string) =>
  message.includes('Inngest API Error') || message.includes('Event key not found');

export const mapSyncIntegrationError = (error: unknown): string => {
  const message = parseTRPCError(error).message;
  if (isRateLimitError(message)) return 'rate_limit';
  if (isInngestError(message)) return 'inngest_unavailable';
  return message;
};

export const createEmptyBulkSyncResult = (): BulkSyncIntegrationsResult => ({
  started: 0,
  failed: [],
});

type BulkSyncDeps = {
  syncShopify: (storeId: string) => Promise<unknown>;
  syncWooCommerce: (storeId: string) => Promise<unknown>;
};

const runStoreSync = async (storeRef: BulkSyncStoreRef, deps: BulkSyncDeps) => {
  if (storeRef.provider === 'woocommerce') {
    await deps.syncWooCommerce(storeRef.id);
    return 'started' as const;
  }
  await deps.syncShopify(storeRef.id);
  return 'started' as const;
};

const runStoreSyncSafe = async (storeRef: BulkSyncStoreRef, deps: BulkSyncDeps) => {
  try {
    const status = await runStoreSync(storeRef, deps);
    return { storeRef, status, reason: undefined };
  } catch (error) {
    return { storeRef, status: 'failed' as const, reason: mapSyncIntegrationError(error) };
  }
};

const aggregateSyncOutcomes = (
  outcomes: Array<{
    storeRef: BulkSyncStoreRef;
    status: 'started' | 'failed';
    reason?: string;
  }>,
): BulkSyncIntegrationsResult => {
  const result = createEmptyBulkSyncResult();
  outcomes.forEach((outcome) => {
    if (outcome.status === 'started') result.started += 1;
    if (outcome.status === 'failed') {
      result.failed.push({
        storeId: outcome.storeRef.id,
        storeName: outcome.storeRef.name,
        reason: outcome.reason ?? 'unknown_error',
      });
    }
  });
  return result;
};

export const processBulkSyncIntegrations = async (
  stores: BulkSyncStoreRef[],
  deps: BulkSyncDeps,
): Promise<BulkSyncIntegrationsResult> => {
  const outcomes = await Promise.all(stores.map((storeRef) => runStoreSyncSafe(storeRef, deps)));
  return aggregateSyncOutcomes(outcomes);
};

export const getProviderLabel = (provider: ProductSyncProvider) =>
  provider === 'woocommerce' ? 'WooCommerce' : 'Shopify';
